import React, { useState, useEffect, useRef, useMemo } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import styles from "./AgentAnalysis.module.css";
import { LineChart, Line, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import {
  API_BASE_URL,
  getAgentCallsByMonth,
  getAppointments,
} from "../../../Store/apiStore";
import { useNavigate } from "react-router-dom";

import {
  useCallHistoryStore,
  useCallHistoryStore1,
} from "../../../Store/useCallHistoryStore ";
import { DateTime } from "luxon";
import decodeToken from "../../../lib/decodeToken";
import useTeamPermissions from "../../../hooks/useTeamPermissions";

// ✅ FIXED: Convert timestamp to date string using specific timezone
function formatDateISOWithTimezone(timestamp, timezone = "UTC") {
  if (!timestamp) return null;
  const dt = DateTime.fromMillis(timestamp).setZone(timezone);
  return dt.toFormat("yyyy-MM-dd");
}

function formatDateISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function toDateSafe(value) {
  if (!value && value !== 0) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const asNum = Number(value);
  if (!Number.isNaN(asNum)) {
    const ms = asNum < 1e12 ? asNum * 1000 : asNum;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

function formatTimeLikeClock(value) {
  const d = toDateSafe(value);
  if (!d) return "00:00";
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}

function joinDateTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const [hh = "00", mm = "00"] = timeStr.split(":");
  return `${dateStr}T${hh.padStart(2, "0")}:${mm.padStart(2, "0")}:00`;
}

const AgentAnalysis = ({ data, callVolume, agentId, calApiKey, eventId }) => {
  const [callHistory, setCallHistory] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [itemsForSelectedDate, setItemsForSelectedDate] = useState([]);
  const [dateMap, setDateMap] = useState({});
  const [chartData, setChartData] = useState([]);
  const bookingsRef = useRef(null);
  const navigate = useNavigate();

  const token = localStorage.getItem("token") || "";
  const userId = decodeToken(token)?.id || "";
  const today = new Date();
  const { canViewCallLogs, canViewAppointments } = useTeamPermissions(60000);

  // console.log(callHistory, "callhistory ");

  const getDateFromTimestamp = (date = new Date()) => {
    const dt = DateTime.fromJSDate(date).setZone(
      callHistory?.timezone || "UTC"
    );

    return {
      dayName: dt.toFormat("EEEE"),
      dateString: dt.toFormat("dd LLLL yyyy"),
      formattedDate: dt.toFormat("yyyy-MM-dd"),
    };
  };

  const { dayName, dateString, formattedDate } = getDateFromTimestamp();

  // ✅ FIXED: Generate chart data from callHistory with proper timezone
  useEffect(() => {
    if (!callHistory || callHistory.length === 0) {
      setChartData(data || []);
      return;
    }

    // Get the timezone from the first call or use UTC
    const timezone = callHistory[0]?.timezone || "UTC";

    // Group calls by date using their timezone
    const callsByDate = callHistory.reduce((acc, call) => {
      const dateKey = formatDateISOWithTimezone(call.start_timestamp, timezone);
      if (!dateKey) return acc;

      if (!acc[dateKey]) {
        acc[dateKey] = 0;
      }
      acc[dateKey]++;
      return acc;
    }, {});

    // Get last 7 days from today in the call's timezone
    const todayInTz = DateTime.now().setZone(timezone);
    const last7Days = [];

    for (let i = 6; i >= 0; i--) {
      const date = todayInTz.minus({ days: i });
      const dateKey = date.toFormat("yyyy-MM-dd");
      const dayName = date.toFormat("EEE"); // Mon, Tue, etc.

      last7Days.push({
        name: dayName,
        calls: callsByDate[dateKey] || 0,
        fullDate: dateKey
      });
    }

    setChartData(last7Days);
  }, [callHistory, data]);

  // ✅ FIXED: Calculate growth based on timezone-aware chart data
  const { growthPercent, growthLabelClass } = useMemo(() => {
    const safeData = Array.isArray(chartData) ? chartData : [];

    if (safeData.length < 7) {
      return { growthPercent: 0, growthLabelClass: styles.positive };
    }

    // Take last 7 days (current week)
    const last7 = safeData.slice(-7);

    // For comparison, we'd need previous 7 days
    // Since we only have current week data, let's compare with average
    const sum = (arr) =>
      arr.reduce((acc, x) => acc + (Number(x?.calls) || 0), 0);

    const last7Total = sum(last7);
    const last7Avg = last7Total / 7;

    // If you have historical data, use it. Otherwise show total growth
    let pct = 0;
    if (last7Total === 0) {
      pct = 0;
    } else {
      // Calculate growth compared to previous period if data exists
      const prev7 = safeData.slice(-14, -7);
      if (prev7.length === 7) {
        const prev7Total = sum(prev7);
        if (prev7Total === 0) {
          pct = last7Total > 0 ? 100 : 0;
        } else {
          pct = ((last7Total - prev7Total) / prev7Total) * 100;
        }
      } else {
        // No previous data, just show current total
        pct = last7Total > 0 ? 100 : 0;
      }
    }

    const rounded = Math.round(pct);
    const cls =
      rounded >= 0 ? styles.positive : styles.negative || styles.positive;

    return { growthPercent: rounded, growthLabelClass: cls };
  }, [chartData]);

  const fetchCallHistory = async () => {
    if (!agentId) {
      setCallHistory([]);
      return;
    }
    try {
      const currentDate = new Date();
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const response = await getAgentCallsByMonth(agentId, month, year);
      setCallHistory(response?.calls || []);
    } catch (error) {
      setCallHistory([]);
    }
  };

  useEffect(() => {
    fetchCallHistory();
  }, [agentId]);

  const fetchCalBookings = async () => {
    if (!calApiKey) return [];
    try {
      const resp = await fetch(
        `https://api.cal.com/v1/bookings?apiKey=${encodeURIComponent(
          calApiKey
        )}`
      );
      if (!resp.ok) throw new Error("Cal.com fetch failed");
      const json = await resp.json();
      let arr = json?.bookings || [];
      if (eventId) {
        arr = arr.filter((b) => Number(b.eventTypeId) === Number(eventId));
      }
      return arr.map((b) => ({
        type: "meeting",
        id: b.id ?? b.uid ?? `cal_${b.startTime}`,
        title: b.title || b.eventType?.title || "Cal Booking",
        startTime: b.startTime,
        endTime: b.endTime,
      }));
    } catch (e) {
      console.error("Cal.com booking error:", e);
      return [];
    }
  };

  const fetchDbAppointments = async () => {
    if (!userId) return [];
    try {
      const res = await getAppointments(userId, agentId || null);
      const list = Array.isArray(res?.data) ? res.data : [];
      return list.map((appt) => {
        const start =
          appt?.startTime ||
          joinDateTime(appt?.appointmentDate, appt?.appointmentTime);
        return {
          type: "db-appointment",
          id: appt.id ?? `db_${start}`,
          title: appt?.reason
            ? `${appt.reason}${appt?.attendeeName ? ` — ${appt.attendeeName}` : ""
            }`
            : `Appointment${appt?.attendeeName ? ` — ${appt.attendeeName}` : ""
            }`,
          startTime: start,
          endTime: null,
          icsFile: appt?.icsFile || null,
          raw: appt,
        };
      });
    } catch (e) {
      console.error("DB appointments error:", e);
      return [];
    }
  };

  // ✅ FIXED: Use timezone when creating dateMap keys
  const rebuildDateMap = async () => {
    const callItems = (callHistory || []).map((c, i) => ({
      type: "call",
      id: c.call_id || `call_${i}`,
      title: c?.custom_analysis_data?.name
        ? `Caller — ${c.custom_analysis_data.name}`
        : "Caller",
      startTime: c.start_timestamp,
      endTime: c.end_timestamp || null,
      timezone: c.timezone || "UTC",
      raw: c,
    }));

    const [calItems, dbItems] = await Promise.all([
      fetchCalBookings(),
      fetchDbAppointments(),
    ]);

    const merged = [...callItems, ...calItems, ...dbItems];

    const map = merged.reduce((acc, item) => {
      let key;

      if (item.type === "call" && item.timezone) {
        key = formatDateISOWithTimezone(item.startTime, item.timezone);
      } else {
        const d = toDateSafe(item.startTime);
        if (!d) return acc;
        key = formatDateISO(d);
      }

      if (!key) return acc;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});

    setDateMap(map);

    const selectedKey = formatDateISO(selectedDate);
    setItemsForSelectedDate(map[selectedKey] || []);
  };

  useEffect(() => {
    rebuildDateMap();
  }, [callHistory, calApiKey, eventId, selectedDate, userId]);

  const handleDateClick = (date) => {
    const calendarKey = formatDateISO(date);
    setSelectedDate(date);
    setItemsForSelectedDate(dateMap[calendarKey] || []);
  };

  const tileContent = ({ date, view }) => {
    if (view !== "month") return null;
    const items = dateMap[formatDateISO(date)] || [];
    const calls = items.filter((x) => x.type === "call").length;
    const bookings = items.filter((x) => x.type !== "call").length;
    const cap = (n) => (n > 99 ? "99+" : n);

    return (
      <div className={styles.bookingDotContainer}>
        {bookings > 0 && (
          <div className={`${styles.dot} ${styles.greenDot}`}>
            {cap(bookings)}
          </div>
        )}
        {calls > 0 && (
          <div className={`${styles.dot} ${styles.orangeDot}`}>
            {cap(calls)}
          </div>
        )}
      </div>
    );
  };

  const handleMonthChange = async (month, year) => {
    try {
      if (agentId) {
        const res = await getAgentCallsByMonth(agentId, month, year);
        setCallHistory(res.calls || []);
      }
    } catch (error) {
      console.error("Error fetching month data:", error);
      setCallHistory([]);
    }
  };

  const getTimeFromTimestamp = (timestamp, timezone) => {
    if (!timestamp) return "-";
    return DateTime.fromMillis(timestamp)
      .setZone(timezone || "UTC")
      .toFormat("hh:mm:ss a");
  };

  const blurCallStyle = !canViewCallLogs ? { filter: "blur(4px)", opacity: 0.5, pointerEvents: "none", userSelect: "none" } : {};
  const blurApptStyle = !canViewAppointments ? { filter: "blur(4px)", opacity: 0.5, pointerEvents: "none", userSelect: "none" } : {};

  return (
    <div className={styles.container}>
      <div style={blurCallStyle} title={!canViewCallLogs ? "You don't have permission to view call logs" : ""}>
        <div className={styles.CallFlex}>
          <div className={styles.callVolume}>
            {callVolume ? callVolume : "0"} <span>Call Volume</span>
          </div>

          <div className={styles.trend}>
            Last 7 Days{" "}
            <span className={growthLabelClass}>
              {growthPercent >= 0 ? `+${growthPercent}%` : `${growthPercent}%`}
            </span>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={100}>
          <LineChart data={chartData}>
            <defs>
              <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow
                  dx="0"
                  dy="2"
                  stdDeviation="3"
                  floodColor="#6A0DAD"
                  floodOpacity="0.4"
                />
              </filter>
            </defs>
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <Tooltip />
            <Line
              type="monotone"
              dataKey="calls"
              stroke="#6A0DAD"
              strokeWidth={2}
              dot={false}
              strokeLinecap="round"
              strokeOpacity={1}
              isAnimationActive={true}
              filter="url(#shadow)"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* <div className={styles.CalendarTopPin}>
        <img src="/svg/Calendar-Top-pin.svg" alt="Calendar-Top-pin" />

        <div className={styles.calendarSection}>
          <h1 className={styles.CalendarTitleWeek}>
            {dayName}
            <p className={styles.CalendarTitleDate}>{dateString}</p>
          </h1>

          <Calendar
            onChange={handleDateClick}
            value={selectedDate}
            tileContent={tileContent}
            onActiveStartDateChange={({ activeStartDate }) => {
              const dt = DateTime.fromJSDate(activeStartDate).setZone(
                callHistory?.timezone || "UTC"
              );

              handleMonthChange(dt.month, dt.year);
            }}
            calendarType="gregory"
            className={styles.reactCalendar}
          />
        </div>
      </div>

      {itemsForSelectedDate.length > 0 && (
        <div className={styles.bookingsList} ref={bookingsRef}>
          <h3>Items for {selectedDate.toDateString()}:</h3>
          <ul>
            {itemsForSelectedDate
              .sort((a, b) => {
                const getTs = (item) => {
                  if (item.type === "call") {
                    return Number(item.raw?.start_timestamp || 0); 
                  }
                  const d = toDateSafe(item.startTime);
                  return d ? d.getTime() : 0;
                };

                return getTs(b) - getTs(a); 
              })


              .map((item) => {
                const isCall = item.type === "call";
                const isDb = item.type === "db-appointment";
                const timeLabel = formatTimeLikeClock(item.startTime);

                return (
                  <li
                    key={`${item.type}_${item.id}`}
                    className={styles.bookingCard}
                    onClick={() => {
                      if (isCall && item.raw?.call_id) {
                        navigate(`/call-details/${item.raw.call_id}`, {
                          state: {
                            agentId: item.raw.agent_id,
                            start_timestamp: item.raw.start_timestamp,
                          },
                        });
                      }
                    }}
                    style={{
                      cursor:
                        isCall && item.raw?.call_id ? "pointer" : "default",
                    }}
                  >
                    <div className={styles.time}>
                      {item.type === "call"
                        ? [
                          getTimeFromTimestamp(
                            item?.raw?.start_timestamp,
                            item?.raw?.timezone
                          ),
                          item?.raw?.end_timestamp
                            ? ` - ${getTimeFromTimestamp(
                              item?.raw?.end_timestamp,
                              item?.raw?.timezone
                            )}`
                            : "",
                        ].join("")
                        : timeLabel}
                    </div>

                    <div className={styles.detailColumn}>
                      <div className={styles.line}>
                        <span className={styles.titleText}>
                          <b>{isCall ? "Caller:" : "Title:"}</b>{" "}
                          {isDb ? item.title : item.title || "N/A"}
                        </span>
                      </div>

                      {isCall && (
                        <div className={styles.timeRange}>
                          <b>Phone:</b>{" "}
                          {item.raw?.call_type === "phone_call"
                            ? item.raw?.from_number
                            : item.raw?.call_type === "api_chat"
                              ? "chat"
                              : item.raw?.chat_type === "api_chat"
                                ? "chat"
                                : item.raw?.call_type}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
          </ul>
        </div>
      )} */}
    </div>
  );
};

export default AgentAnalysis;